import React from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  IconButton,
  useTheme
} from '@mui/material';
import { Link } from 'react-router-dom';
import { 
  WhatsApp as WhatsAppIcon, 
  Phone as PhoneIcon, 
  Email as EmailIcon,
  Place as PlaceIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../utils/LanguageContext';
import contactInfo from '../../config/contact';

const Footer: React.FC = () => {
  const { t } = useTranslation();
  const { language } = useLanguage();
  
  // WhatsApp link
  const whatsappUrl = `https://wa.me/${contactInfo.whatsapp.number}?text=${encodeURIComponent(contactInfo.whatsapp.message[language as keyof typeof contactInfo.whatsapp.message])}`;
  
  return (
    <Box 
      component="footer" 
      sx={{ 
        bgcolor: 'background.paper', 
        py: 6,
        borderTop: '1px solid rgba(255, 255, 255, 0.1)', 
      }}
    >
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4, justifyContent: 'space-between' }}>
          {/* Company Info */}
          <Box sx={{ width: { xs: '100%', sm: '45%', md: '30%' } }}>
            <Typography variant="h6" color="primary" gutterBottom>
              {contactInfo.company.name[language as keyof typeof contactInfo.company.name]}
            </Typography>
            
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'flex-start' }}>
              <PlaceIcon sx={{ mr: 1, mt: 0.5, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary">
                {contactInfo.company.address[language as keyof typeof contactInfo.company.address]}
              </Typography>
            </Box>
            
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
              <PhoneIcon sx={{ mr: 1, color: 'text.secondary' }} />
              <Box
                component="a"
                href={`tel:${contactInfo.company.phone}`} 
                sx={{ color: 'text.secondary', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
              >
                {contactInfo.company.phone}
              </Box>
            </Box>
            
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
              <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} />
              <Box
                component="a"
                href={`mailto:${contactInfo.company.email}`} 
                sx={{ color: 'text.secondary', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
              >
                {contactInfo.company.email}
              </Box>
            </Box>
            
            <Box>
              <IconButton 
                color="primary" 
                aria-label="WhatsApp" 
                component="a" 
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                <WhatsAppIcon />
              </IconButton>
            </Box>
          </Box>
          
          {/* Quick Links */}
          <Box sx={{ width: { xs: '100%', sm: '45%', md: '30%' } }}>
            <Typography variant="h6" color="primary" gutterBottom>
              {t('nav.quickOrder')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Box 
                component={Link} 
                to="/" 
                sx={{ color: 'text.secondary', textDecoration: 'none', '&:hover': { textDecoration: 'underline' }, mb: 1 }}
              >
                {t('nav.home')}
              </Box>
              <Box 
                component={Link} 
                to="/services" 
                sx={{ color: 'text.secondary', textDecoration: 'none', '&:hover': { textDecoration: 'underline' }, mb: 1 }}
              >
                {t('nav.services')}
              </Box>
              <Box 
                component={Link} 
                to="/quick-order" 
                sx={{ color: 'text.secondary', textDecoration: 'none', '&:hover': { textDecoration: 'underline' }, mb: 1 }}
              >
                {t('nav.quickOrder')}
              </Box>
              <Box 
                component={Link} 
                to="/faq" 
                sx={{ color: 'text.secondary', textDecoration: 'none', '&:hover': { textDecoration: 'underline' }, mb: 1 }}
              >
                {t('nav.faq')}
              </Box>
              <Box 
                component={Link} 
                to="/contact" 
                sx={{ color: 'text.secondary', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
              >
                {t('nav.contact')}
              </Box>
            </Box>
          </Box>
          
          {/* Legal Links */}
          <Box sx={{ width: { xs: '100%', sm: '45%', md: '30%' } }}>
            <Typography variant="h6" color="primary" gutterBottom>
              {t('legal.privacy')} & {t('legal.terms')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Box 
                component={Link} 
                to="/privacy-policy" 
                sx={{ color: 'text.secondary', textDecoration: 'none', '&:hover': { textDecoration: 'underline' }, mb: 1 }}
              >
                {t('legal.privacy')}
              </Box>
              <Box 
                component={Link} 
                to="/terms-of-service" 
                sx={{ color: 'text.secondary', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
              >
                {t('legal.terms')}
              </Box>
            </Box>
          </Box>
        </Box>
        
        {/* Copyright */}
        <Box sx={{ mt: 5, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {t('footer.copyright')}
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer; 